import React, { useState, useEffect, useCallback } from 'react';
import { calculateSalary } from '../services/api';
import { useAuth } from '../context/AuthContext';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(amount);
};

const SalaryCalculator = () => {
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('2025-12');
  const [salaryPercentage, setSalaryPercentage] = useState(20);

  const fetchSalaryData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await calculateSalary(selectedMonth, salaryPercentage);
      setData(response.data);
    } catch (err) {
      setError('Failed to calculate salary');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, salaryPercentage]);

  useEffect(() => {
    fetchSalaryData();
  }, [fetchSalaryData]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  return (
    <div>
      {/* Header for non-admin users */}
      {!isAdmin && (
        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
          <h2 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>
            Welcome, {user?.full_name}
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Your salary statement for {getMonthName(selectedMonth)}
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>Month:</label>
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            <option value="2025-12">December 2025</option>
            <option value="2025-11">November 2025</option>
            <option value="2025-10">October 2025</option>
          </select>
        </div>

        {isAdmin && (
          <div className="filter-group">
            <label>Salary Percentage:</label>
            <input
              type="number"
              value={salaryPercentage}
              onChange={(e) => setSalaryPercentage(parseFloat(e.target.value) || 0)}
              min="0"
              max="100"
              step="0.5"
              style={{ width: '80px' }}
            />
            <span>%</span>
          </div>
        )}
      </div>

      {/* Salary Highlight */}
      <div className="salary-highlight">
        <div className="salary-title">
          {isAdmin ? "Madame Deepti Balagopal's Salary" : 'Your Calculated Salary'}
        </div>
        <div className="salary-amount">{formatCurrency(data.salaryAmount)}</div>
        <div className="salary-formula">{data.calculation.details}</div>
      </div>

      {/* Calculation Breakdown */}
      <div className="card">
        <h3 className="card-title">Calculation Breakdown</h3>
        
        <div className="table-container">
          <table>
            <tbody>
              {isAdmin && (
                <>
                  <tr>
                    <td><strong>Total Income (All Sources)</strong></td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(data.totalIncome)}</td>
                  </tr>
                  <tr style={{ color: 'var(--danger-color)' }}>
                    <td>Less: FD Interest (Passive Income)</td>
                    <td style={{ textAlign: 'right' }}>- {formatCurrency(data.fdInterest)}</td>
                  </tr>
                  <tr style={{ color: 'var(--danger-color)' }}>
                    <td>Less: Internal Transfers</td>
                    <td style={{ textAlign: 'right' }}>- {formatCurrency(data.internalTransfers)}</td>
                  </tr>
                  <tr style={{ color: 'var(--danger-color)' }}>
                    <td>Less: FD Maturity Proceeds</td>
                    <td style={{ textAlign: 'right' }}>- {formatCurrency(data.fdMaturity)}</td>
                  </tr>
                </>
              )}
              <tr style={{ background: 'var(--background-color)', fontWeight: 'bold' }}>
                <td>= Active Income (Turnover)</td>
                <td style={{ textAlign: 'right', color: 'var(--secondary-color)' }}>
                  {formatCurrency(data.activeIncome)}
                </td>
              </tr>
              <tr>
                <td>Salary Percentage</td>
                <td style={{ textAlign: 'right' }}>{data.salaryPercentage}%</td>
              </tr>
              <tr style={{ background: 'var(--accent-color)', color: 'white', fontWeight: 'bold' }}>
                <td>= Calculated Salary</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(data.salaryAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Company-wise Breakdown */}
      <div className="card">
        <h3 className="card-title">Company-wise Active Income</h3>
        
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th style={{ textAlign: 'right' }}>Active Income</th>
                <th style={{ textAlign: 'right' }}>Contribution</th>
              </tr>
            </thead>
            <tbody>
              {data.companyBreakdown.map((company) => (
                <tr key={company.code}>
                  <td>
                    <strong>{company.name}</strong>
                    <span className="company-code">{company.code}</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(company.activeIncome)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {data.activeIncome > 0 
                      ? ((company.activeIncome / data.activeIncome) * 100).toFixed(1) + '%'
                      : '0%'}
                  </td>
                </tr>
              ))}
              <tr style={{ fontWeight: 'bold', background: 'var(--background-color)' }}>
                <td>TOTAL</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(data.activeIncome)}</td>
                <td style={{ textAlign: 'right' }}>100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Explanation */}
      {isAdmin && (
        <div className="card" style={{ background: 'linear-gradient(135deg, #ebf8ff 0%, #e6fffa 100%)' }}>
          <h3 className="card-title">📝 Salary Calculation Rules</h3>
          <ul style={{ lineHeight: '2', paddingLeft: '20px' }}>
            <li><strong>Active Income</strong> = Total Revenue excluding FD Interest, Internal Transfers, and FD Maturity</li>
            <li><strong>Passive Income</strong> = Interest earned from Fixed Deposits</li>
            <li><strong>Internal Transfers</strong> = Money moved between the four HERE AND NOW companies (excluded from turnover)</li>
            <li><strong>Salary Formula</strong> = Active Income × {salaryPercentage}%</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default SalaryCalculator;
